// src/lib/deviceFingerprint.ts
// ────────────────────────────────────────────────────────────────────────────
// Deterministic browser/device fingerprint for stable device identity.
//
// Produces a 12-hex-char fingerprint (48 bits) from multiple stable signals
// that survive localStorage / sessionStorage clears.  The fingerprint is
// deterministic: the same browser + hardware + OS always produces the same
// hash across sessions, tabs, and page refreshes.
//
// Signal surface:
//   ┌─────────────────────────┬───────────────────────────────────────────┐
//   │ Signal                  │ Stability                                 │
//   ├─────────────────────────┼───────────────────────────────────────────┤
//   │ Canvas 2D rendering     │ GPU + driver + OS font stack dependent.   │
//   │ WebGL renderer / vendor │ GPU + driver identifier.                  │
//   │ Screen geometry         │ width × height × pixelRatio × colorDepth.│
//   │ Timezone                │ Changes rarely (user relocation).         │
//   │ Platform                │ OS family string.                         │
//   │ Language(s)             │ Browser language preference list.         │
//   │ CPU cores               │ Hardware concurrency count.               │
//   │ Device memory           │ RAM estimate (Chromium only, optional).   │
//   │ Touch support           │ Capacitive digitizer presence.            │
//   │ Audio context           │ Audio processing fingerprint.             │
//   └─────────────────────────┴───────────────────────────────────────────┘
//
// Privacy note: this fingerprint is used ONLY for device identity within
// the PACMaster device registry.  It is never sent to third parties.
// All signals come from standard Web APIs already exposed by the browser.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Compute a deterministic 12-hex-char fingerprint from stable browser
 * signals.  Returns the same value every time on the same device/browser,
 * even if localStorage is cleared.
 *
 * Falls back to a degraded fingerprint (fewer signals) if specific APIs
 * are unavailable (e.g. WebGL disabled, OffscreenCanvas missing).
 */
export async function computeBrowserFingerprint(): Promise<string> {
  const signals: string[] = [];

  // ── 1. Canvas 2D rendering ────────────────────────────────────────────
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      // Background
      ctx.fillStyle = '#f8e71c';
      ctx.fillRect(0, 0, 200, 50);
      // Text with specific font stack — rendering varies by GPU/driver/font
      ctx.fillStyle = '#069';
      ctx.font = '14px Arial, sans-serif';
      ctx.fillText('PACMaster/fp·1.0', 2, 2);
      // Composite blending
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillRect(75, 0, 50, 50);
      // Emoji rendering (varies significantly across platforms)
      ctx.fillStyle = '#f00';
      ctx.font = '18px serif';
      ctx.fillText('🔐', 4, 30);
      signals.push(canvas.toDataURL());
    }
  } catch {
    // Canvas unavailable (rare — headless/server-side)
  }

  // ── 2. WebGL renderer + vendor ────────────────────────────────────────
  try {
    const gl =
      document.createElement('canvas').getContext('webgl') ??
      document.createElement('canvas').getContext('experimental-webgl');
    if (gl) {
      const dbg = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (dbg) {
        const renderer = (gl as WebGLRenderingContext).getParameter(dbg.UNMASKED_RENDERER_WEBGL);
        const vendor = (gl as WebGLRenderingContext).getParameter(dbg.UNMASKED_VENDOR_WEBGL);
        signals.push(`webgl:${vendor}|${renderer}`);
      }
    }
  } catch {
    // WebGL disabled or context creation failed
  }

  // ── 3. Screen geometry ────────────────────────────────────────────────
  try {
    const s = window.screen;
    const dpr = window.devicePixelRatio ?? 1;
    signals.push(`screen:${s.width}x${s.height}x${s.colorDepth}@${dpr}`);
  } catch {
    /* n/a */
  }

  // ── 4. Timezone ───────────────────────────────────────────────────────
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    signals.push(`tz:${tz}`);
  } catch {
    /* n/a */
  }

  // ── 5. Platform ───────────────────────────────────────────────────────
  try {
    const platform =
      navigator.platform ??
      (navigator as any).userAgentData?.platform ??
      '';
    signals.push(`plat:${platform}`);
  } catch {
    /* n/a */
  }

  // ── 6. Languages ──────────────────────────────────────────────────────
  try {
    const langs = navigator.languages?.join(',') ?? navigator.language ?? '';
    signals.push(`lang:${langs}`);
  } catch {
    /* n/a */
  }

  // ── 7. Hardware concurrency (CPU cores) ───────────────────────────────
  try {
    signals.push(`cores:${navigator.hardwareConcurrency ?? 0}`);
  } catch {
    /* n/a */
  }

  // ── 8. Device memory (Chromium only) ──────────────────────────────────
  try {
    const mem = (navigator as any).deviceMemory;
    if (typeof mem === 'number') signals.push(`mem:${mem}`);
  } catch {
    /* n/a */
  }

  // ── 9. Touch support ─────────────────────────────────────────────────
  try {
    const maxTouch = navigator.maxTouchPoints ?? 0;
    signals.push(`touch:${maxTouch}`);
  } catch {
    /* n/a */
  }

  // ── 10. Audio context fingerprint ─────────────────────────────────────
  try {
    const AudioCtx =
      window.AudioContext ?? (window as any).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const oscillator = ctx.createOscillator();
      const analyser = ctx.createAnalyser();
      const gain = ctx.createGain();
      const scriptProcessor = ctx.createScriptProcessor(4096, 1, 1);

      gain.gain.value = 0; // muted
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(10000, ctx.currentTime);

      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(0);

      // Collect a sample from the analyser
      const data = new Float32Array(analyser.frequencyBinCount);
      analyser.getFloatFrequencyData(data);
      // Use first 30 bins as a fingerprint signal
      const audioSig = Array.from(data.slice(0, 30))
        .map((v) => (Number.isFinite(v) ? v.toFixed(2) : '0'))
        .join(',');
      signals.push(`audio:${audioSig}`);

      oscillator.stop();
      scriptProcessor.disconnect();
      analyser.disconnect();
      gain.disconnect();
      ctx.close().catch(() => {});
    }
  } catch {
    // AudioContext unavailable or failed
  }

  // ── Hash all signals ──────────────────────────────────────────────────
  const raw = signals.join('||');

  // Use SubtleCrypto (available in all modern secure contexts) for SHA-256.
  // Fall back to a simple djb2 variant if SubtleCrypto is unavailable.
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(raw);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hex.slice(0, 12); // 48 bits → 12 hex chars
  } catch {
    // Fallback: djb2-xor hash → 12 hex chars
    let h1 = 5381;
    let h2 = 52711;
    for (let i = 0; i < raw.length; i++) {
      const c = raw.charCodeAt(i);
      h1 = ((h1 << 5) + h1 + c) >>> 0;
      h2 = ((h2 << 5) + h2 + c) >>> 0;
    }
    const fallback = h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0');
    return fallback.slice(0, 12);
  }
}

/**
 * Multi-layer persistent device identity.
 *
 * Storage hierarchy (first available wins on read, ALL are written on create):
 *   1. IndexedDB  (most persistent — survives "Clear Site Data" in most browsers)
 *   2. localStorage
 *   3. sessionStorage (in-tab session — last resort before fingerprint)
 *
 * If ALL storage layers are empty (first visit or full data wipe), the
 * fingerprint is used as the canonical device identifier — guaranteeing
 * the same device always resolves to the same device_id.
 */

const IDB_DB_NAME = 'pac_device_identity';
const IDB_STORE_NAME = 'identity';
const IDB_KEY = 'device_id';
const LS_KEY = 'pac_fe_peer_device_id';
const SS_KEY = 'pac_fe_peer_device_id_session';

/** Read from IndexedDB (resolves null if unavailable). */
function readIdb(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
          db.createObjectStore(IDB_STORE_NAME);
        }
      };
      req.onsuccess = () => {
        try {
          const db = req.result;
          const tx = db.transaction(IDB_STORE_NAME, 'readonly');
          const store = tx.objectStore(IDB_STORE_NAME);
          const get = store.get(IDB_KEY);
          get.onsuccess = () => resolve(typeof get.result === 'string' ? get.result : null);
          get.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Write to IndexedDB (best-effort). */
function writeIdb(value: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
          db.createObjectStore(IDB_STORE_NAME);
        }
      };
      req.onsuccess = () => {
        try {
          const db = req.result;
          const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
          const store = tx.objectStore(IDB_STORE_NAME);
          store.put(value, IDB_KEY);
          tx.oncomplete = () => resolve();
          tx.onerror = () => resolve();
        } catch {
          resolve();
        }
      };
      req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

function readLs(): string | null {
  try { return localStorage.getItem(LS_KEY); } catch { return null; }
}
function writeLs(v: string): void {
  try { localStorage.setItem(LS_KEY, v); } catch { /* noop */ }
}

function readSs(): string | null {
  try { return sessionStorage.getItem(SS_KEY); } catch { return null; }
}
function writeSs(v: string): void {
  try { sessionStorage.setItem(SS_KEY, v); } catch { /* noop */ }
}

const DEVICE_ID_PREFIX = 'fe-peer-';

/**
 * Resolve or create a stable device identity.
 *
 * Resolution order:
 *   1. IndexedDB (most durable)
 *   2. localStorage
 *   3. sessionStorage
 *   4. Generate from browser fingerprint (deterministic — same device
 *      always produces the same ID even after full data wipe)
 *
 * On resolution, the ID is back-filled to ALL available storage layers
 * for future reads.
 */
export async function getOrCreateStableDeviceId(): Promise<string> {
  // ── 1. Try IndexedDB ──────────────────────────────────────────────────
  const fromIdb = await readIdb();
  if (fromIdb?.startsWith(DEVICE_ID_PREFIX)) {
    // Back-fill lower layers
    writeLs(fromIdb);
    writeSs(fromIdb);
    return fromIdb;
  }

  // ── 2. Try localStorage ───────────────────────────────────────────────
  const fromLs = readLs();
  if (fromLs?.startsWith(DEVICE_ID_PREFIX)) {
    // Back-fill all layers
    void writeIdb(fromLs);
    writeSs(fromLs);
    return fromLs;
  }

  // ── 3. Try sessionStorage ─────────────────────────────────────────────
  const fromSs = readSs();
  if (fromSs?.startsWith(DEVICE_ID_PREFIX)) {
    // Back-fill durable layers
    void writeIdb(fromSs);
    writeLs(fromSs);
    return fromSs;
  }

  // ── 4. Generate from fingerprint (deterministic) ──────────────────────
  const fingerprint = await computeBrowserFingerprint();
  const id = `${DEVICE_ID_PREFIX}${fingerprint}`;

  // Persist to ALL layers
  void writeIdb(id);
  writeLs(id);
  writeSs(id);

  return id;
}
